import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { KeyRound, Copy, Trash2, TerminalSquare } from "lucide-react";
import { toast } from "sonner";

export default function ApiTokensPanel() {
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState({ read: true, remediate: false });
  const [fresh, setFresh] = useState(null); // shown once
  const [creating, setCreating] = useState(false);

  const load = () => api.tokens.list().then(setTokens).catch(console.error);
  useEffect(() => { load(); }, []);

  const create = async () => {
    const selected = Object.entries(scopes).filter(([, v]) => v).map(([k]) => k);
    if (!selected.length) {
      toast.error("Select at least one scope");
      return;
    }
    setCreating(true);
    try {
      const t = await api.tokens.create(name || "unnamed token", selected);
      setFresh(t);
      setName("");
      load();
      toast.success("Token created", { description: "Copy it now — it will never be shown again." });
    } catch (e) {
      toast.error("Create failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.tokens.revoke(id);
      toast.success("Token revoked");
      load();
    } catch (e) {
      toast.error("Revoke failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const copy = async (text, what = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(what);
    } catch {
      toast.error("Copy failed");
    }
  };

  const curlSnippet = `# Discover tools\ncurl -H "Authorization: Bearer <YOUR_TOKEN>" ${API}/mcp/tools\n\n# Call a tool\ncurl -X POST ${API}/mcp/call \\\n  -H "Authorization: Bearer <YOUR_TOKEN>" \\\n  -H "Content-Type: application/json" \\\n  -d '{"name":"list_opportunities","arguments":{"limit":5}}'`;

  return (
    <div className="cs-card p-5" data-testid="api-tokens-panel">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="w-4 h-4 text-[#00579e]" />
        <h2 className="cs-heading text-base font-semibold">API Tokens & MCP Bridge</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Scoped machine tokens for IDE agents (Claude Code, Cursor) and scripts. Tokens are SHA-256-hashed at rest and shown exactly once.
      </p>

      {/* create */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Token name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., claude-code laptop"
            data-testid="token-name-input" className="rounded-sm h-9 text-xs mt-1 w-[220px]" />
        </div>
        {["read", "remediate"].map((s) => (
          <label key={s} className="flex items-center gap-1.5 text-xs pb-2 cursor-pointer">
            <Checkbox checked={scopes[s]} onCheckedChange={(v) => setScopes({ ...scopes, [s]: !!v })} data-testid={`scope-${s}`} />
            <span className="cs-mono">{s}</span>
          </label>
        ))}
        <Button onClick={create} disabled={creating} data-testid="create-token-button"
          className="rounded-full h-9 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-5">
          {creating ? "Creating…" : "Create Token"}
        </Button>
      </div>

      {/* shown-once token */}
      {fresh && (
        <div className="border border-[#15803d]/40 bg-[#f0fdf4] rounded-sm p-3 mb-4" data-testid="fresh-token-box">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[#15803d] font-semibold mb-1">
            New token — copy now, it will never be shown again
          </div>
          <div className="flex items-center gap-2">
            <code className="cs-mono text-[11px] break-all flex-1" data-testid="fresh-token-value">{fresh.token}</code>
            <Button size="sm" variant="outline" onClick={() => copy(fresh.token, "Token copied")} data-testid="copy-fresh-token"
              className="rounded-sm h-7 text-xs shrink-0">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
        </div>
      )}

      {/* list */}
      <table className="w-full text-xs mb-5">
        <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
          <tr className="text-left">
            <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Name</th>
            <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Prefix</th>
            <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Scopes</th>
            <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Last used</th>
            <th className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Status</th>
            <th className="px-3 py-2"></th>
          </tr>
        </thead>
        <tbody>
          {tokens.length === 0 && (
            <tr><td colSpan={6} className="px-3 py-5 text-center text-muted-foreground" data-testid="no-tokens">No tokens yet.</td></tr>
          )}
          {tokens.map((t) => (
            <tr key={t.id} className="border-b border-border last:border-0" data-testid={`token-row-${t.id}`}>
              <td className="px-3 py-2 font-medium">{t.name}</td>
              <td className="px-3 py-2 cs-mono text-muted-foreground">{t.prefix}…</td>
              <td className="px-3 py-2 cs-mono">{(t.scopes || []).join(", ")}</td>
              <td className="px-3 py-2 text-muted-foreground">{t.last_used_at ? new Date(t.last_used_at).toLocaleString() : "never"}</td>
              <td className="px-3 py-2">
                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${t.revoked ? "bg-[#fee2e2] text-[#b91c1c]" : "bg-[#dcfce7] text-[#15803d]"}`}>
                  {t.revoked ? "revoked" : "active"}
                </span>
              </td>
              <td className="px-3 py-2 text-right">
                {!t.revoked && (
                  <button onClick={() => revoke(t.id)} data-testid={`revoke-token-${t.id}`}
                    className="text-muted-foreground hover:text-[#b91c1c] transition-colors duration-200">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* connection snippet */}
      <div className="flex items-center gap-2 mb-2">
        <TerminalSquare className="w-3.5 h-3.5 text-[#00579e]" />
        <span className="text-xs font-semibold">Connect an agent</span>
        <Button size="sm" variant="outline" onClick={() => copy(curlSnippet, "Snippet copied")} data-testid="copy-mcp-snippet"
          className="rounded-sm h-6 text-[10px] ml-auto">
          <Copy className="w-3 h-3 mr-1" /> Copy
        </Button>
      </div>
      <pre className="cs-mono text-[10px] bg-[#0B0B0F] text-[#E2E8F0] p-3 rounded-sm overflow-x-auto leading-relaxed" data-testid="mcp-snippet">
        {curlSnippet}
      </pre>
    </div>
  );
}
